<template>
  <div
    data-product-tour="step-4"
    id="side-menu"
    class="side-menu"
    :class="{
      'side-menu--open': !isMenuClosed,
      'side-menu--closed': isMenuClosed,
      'side-menu--mobile': isMobile,
    }"
    @transitionend="handleTransitionEnd"
  >
    <nav v-on-click-outside="closeSubmenuCompact" class="side-menu__menu">
      <template v-for="menuElement in menu" :key="menuElement.name">
        <SideMenuLockedItem
          v-if="isMenuLocked(menuElement)"
          :name="menuElement.displayName || menuElement.name"
          :to="menuElement.locked_route?.path || menuElement.path"
          :icon-class="menuElement.iconClass"
          :icon-class-active="menuElement.iconClassActive"
          :is-menu-collapsed="isMenuClosed"
          :is-transition-ended="isTransitionEnded"
        />
        <SideMenuCollapseItem
          v-else
          :tab="menuElement"
          :active-dropdown="activeDropdown"
          :is-menu-collapsed="isMenuClosed"
          :is-transition-ended="isTransitionEnded"
          @toggle-dropdown="toggleDropdown"
        />

        <Transition name="slide-fade">
          <div
            v-if="activeDropdown === menuElement.name"
            class="side-menu__submenu"
            :class="{ 'side-menu__submenu--compact': isMenuClosed }"
          >
            <span
              v-if="isMenuClosed"
              :class="{ 'side-menu__submenu--compact__title': isMenuClosed }"
            >
              {{ menuElement.name }}
            </span>

            <RouterLink
              v-for="subTab in getFilteredSubTabs(menuElement)"
              :key="subTab.path"
              v-focus
              :to="subTab.path"
              class="title-small-regular side-menu__submenu__item"
              :class="{
                'side-menu__submenu--compact__text': isMenuClosed,
              }"
              @click="closeSubmenuCompact"
              @keydown.enter="closeSubmenuCompact"
            >
              <div class="side-menu__submenu__item__content">
                {{ subTab.displayName || subTab.name }}
                <div
                  v-if="isSubTabLocked(subTab)"
                  class="side-menu__submenu__locked-icon"
                >
                  <PermissionLevelIcon
                    :permission-level="subTab.meta.permission_level"
                  />
                </div>
              </div>
            </RouterLink>
          </div>
        </Transition>
      </template>
    </nav>

    <button v-focus class="side-menu__menu-expand-button" @click="handleToggle">
      <i
        class="fa"
        :class="isMenuClosed ? 'fa-chevrons-right' : 'fa-chevrons-left'"
      ></i>
      <span
        v-if="!isMenuClosed && isTransitionEnded"
        class="title-medium-regular side-menu__menu-expand-button__text"
      >
        Réduire le menu
      </span>
    </button>
  </div>
</template>

<script setup>
import { vOnClickOutside } from "@vueuse/components";

import { useSideMenu } from "@follaw-fr/composables/useSideMenu";
import menu from "@follaw-fr/utils/side_menu";
import SideMenuCollapseItem from "@follaw-fr/components/sidemenu/molecules/SideMenuCollapseItem.vue";
import SideMenuLockedItem from "@follaw-fr/components/sidemenu/molecules/SideMenuLockedItem.vue";

import { ref } from "vue";
import PermissionLevelIcon from "@follaw-fr/components/general/atoms/PermissionLevelIcon.vue";
import { useUserSettingsStore } from "@follaw-fr/stores/user-settings";
const userSettingsStore = useUserSettingsStore();

const {
  isMenuClosed,
  activeDropdown,
  toggleMenuState,
  toggleDropdown,
  isMobile,
} = useSideMenu();

const isTransitionEnded = ref(true);

const handleTransitionEnd = () => {
  isTransitionEnded.value = true;
};

const handleToggle = () => {
  toggleMenuState();
  isTransitionEnded.value = false;
};

const closeSubmenuCompact = () => {
  if (isMenuClosed.value) {
    toggleDropdown();
  }
};

const getFilteredSubTabs = (menuElement) => {
  if (!menuElement.secondary_tabs) return [];

  return menuElement.secondary_tabs.filter((subTab) => {
    return subTab.name && subTab.path && !subTab.hidden;
  });
};

const isMenuLocked = (menuElement) =>
  menuElement.meta?.permission_level &&
  !userSettingsStore.hasPermission(menuElement.meta.permission_level);

const isSubTabLocked = (subTab) =>
  subTab.meta?.permission_level &&
  !userSettingsStore.hasPermission(subTab.meta.permission_level);
</script>

<style lang="scss" scoped>
.side-menu {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: width 0.2s ease-in-out;
  overflow: hidden;
  padding: 4px;

  &--open {
    width: 255px;
    align-items: flex-start;
  }

  &--closed {
    width: 55px;
  }

  &--mobile {
    justify-content: flex-start;
  }
}
.side-menu__menu {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 3px; // for display outline top border on first item
}

.side-menu__submenu {
  display: flex;
  flex-direction: column;

  &__locked-icon {
    width: 20px;
    height: 20px;
  }

  &__item {
    padding: 0.5rem 4px 0.5rem 1rem;
    color: var(--clr-n1);

    &:hover {
      color: var(--clr-n1);
      background-color: var(--clr-p4);
    }

    &__content {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
}

.side-menu__submenu--compact {
  position: absolute;
  left: 40px;
  width: 200px;
  z-index: 999;

  &__title {
    padding: 8px 8px 8px 16px;
    margin-bottom: 4px;
    border-radius: 5px;
    color: var(--clr-n8);
    background-color: #fff;
    box-shadow: 0px 3px 5px 0px rgba(0, 0, 0, 0.16);
  }

  &__text {
    color: var(--clr-n8);
    background-color: #fff;
    box-shadow: 0px 3px 5px 0px rgba(0, 0, 0, 0.16);

    &:hover {
      background-color: var(--clr-p2);
      font-weight: bold;
      color: var(--clr-n8);
    }
  }

  .side-menu__submenu__item:nth-child(2) {
    border-radius: 5px 5px 0 0;
  }

  .side-menu__submenu__item:last-child {
    border-radius: 0 0 5px 5px;
  }
}

.side-menu__menu-expand-button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0.5rem 1rem;
  margin-bottom: 3px;
  height: 50px;

  &__text {
    color: var(--clr-n1);
  }
}

.router-link-active {
  font-weight: bold;
  color: var(--clr-n1);
  background-color: var(--clr-p8);
  &:hover {
    background-color: var(--clr-p8);
    color: var(--clr-n1);
  }
}
</style>
